import { Tooltip } from "antd";

/** Loopback hosts — `localhost` (and its *.localhost subdomains), the IPv4
 * 127/8 range and bracketed IPv6 `[::1]`, each with an optional port. These
 * keep the builder PARENT's scheme for their targetOrigin (the original
 * behavior — no receiver or local-transport contract pins them to http). */
const LOOPBACK_HOST_PATTERN = /^(?:(?:[a-z0-9-]+\.)*localhost|127(?:\.\d{1,3}){3}|\[::1\])(?::\d{1,5})?$/i;

/** URL component/separator characters that cannot occur in a bare host. Their
 * presence means `domain` is a URL fragment — a scheme, protocol-relative
 * "//host", path, query, fragment, userinfo or embedded whitespace — which is
 * out of the bare-host caller contract and must never become a post target.
 * (Needed explicitly: the WHATWG URL parser IGNORES extra slashes, so
 * "https:////host" alone would parse as a clean origin.) */
const HOST_SEPARATOR_PATTERN = /[/\\?#@\s]/;

/** Resolve the postMessage targetOrigin for a site domain (the bare host or
 * `host:port` shape every current caller passes via `selectedProject.domain`):
 * remote hosts get the canonical HTTPS site origin — the same fixed scheme
 * src/services/SiteHealthCheck.js (`https://${domain}`), the widget editor
 * frame (`https://${siteUrl}`) and Customize's inbound listener
 * (`https://${domain}`) use — while loopback hosts keep the parent's scheme.
 * A missing domain, or one carrying a scheme, "//", path, query, fragment or
 * userinfo, is out of the bare-host caller contract and resolves to null
 * (fail closed through a standard URL parse — no new throws), so it can
 * never become a post target. Every pre-existing valid host shape stays
 * resolvable (dotted/punycode/IDN hosts, single-segment hosts, IPv4/IPv6
 * literals, ports, trailing dots) — no invented DNS grammar.
 * Exported as the explicit parent-protocol seam for the focused unit matrix;
 * production callers always go through Utils.postMessage. */
export const resolvePostMessageTargetOrigin = (domain, parentProtocol = window.location.protocol) => {
	const host = typeof domain === "string" ? domain.trim() : "";
	if (!host || HOST_SEPARATOR_PATTERN.test(host)) {
		return null;
	}
	const origin = LOOPBACK_HOST_PATTERN.test(host) ? `${parentProtocol}//${host}` : `https://${host}`;
	let parsed;
	try {
		parsed = new URL(origin);
	} catch {
		return null;
	}
	if (parsed.pathname !== "/" || parsed.search || parsed.hash || parsed.username || parsed.password) {
		return null;
	}
	return origin;
};

/** Utils namespace
 * @namespace
 */
export const Utils = {
	/** Post message to the provided domain
	 * @param {string} domain - The bare site host (`host` or `host:port`) where the message will be posted
	 * @param {string} actionType - Action type
	 * @param {string} payload - Payload
	 */

	postMessage: (domain, actionType, payload, win = window.frames.target) => {
		const data = {
			action: actionType,
			payload,
		};
		// The targetOrigin must be the SITE's own origin: the browser's origin
		// check and the receiver's allowlist both silently drop scheme-mismatched
		// messages (an http localhost builder previewing a remote HTTPS site must
		// address it as https, not with the builder parent's http).
		const targetOrigin = resolvePostMessageTargetOrigin(domain);
		if (!targetOrigin) {
			// Missing domain (e.g. the failed-project-load hop `project && project.domain`
			// in Customize.js) or an out-of-contract shape: post to no window, never throw.
			return;
		}
		const widgetFrame = document.getElementById("widgetFrame");
		if (widgetFrame && widgetFrame.contentWindow && actionType !== "language" && actionType !== "reload") {
			// language change causes unsaved items loss
			widgetFrame.contentWindow.postMessage(JSON.stringify(data), targetOrigin);
			// window.frames[0] && window.frames[0].postMessage(JSON.stringify(data), `${window.location.protocol}//${domain}`)
		}
		if (win && typeof win.postMessage === "function") {
			win.postMessage(JSON.stringify(data), targetOrigin);
		}
	},

	getYoutubeId(url) {
		const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
		const match = url.match(regExp);

		return match && match[2].length === 11 ? match[2] : null;
	},

	removeEmpty(obj) {
		return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null));
	},

	checkTimeZone(z) {
		return z > 0 ? `-${z}` : z * -1;
	},

	isObject(item) {
		return item && typeof item === "object" && !Array.isArray(item);
	},

	mergeDeep(target, source) {
		const output = { ...target };
		if (Utils.isObject(target) && Utils.isObject(source)) {
			Object.keys(source).forEach(key => {
				if (Utils.isObject(source[key])) {
					if (!(key in target)) Object.assign(output, { [key]: source[key] });
					else output[key] = Utils.mergeDeep(target[key], source[key]);
				} else {
					Object.assign(output, { [key]: source[key] });
				}
			});
		}
		return output;
	},

	parseQuery(queryString) {
		const query = {};
		const pairs = (queryString[0] === "?" ? queryString.substr(1) : queryString).split("&");
		for (let i = 0; i < pairs.length; i++) {
			const pair = pairs[i].split("=");
			query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || "");
		}
		return query;
	},

	checkTime(i) {
		if (i < 10) {
			i = `0${i}`;
		}
		return i;
	},

	getTimeZone() {
		const date = new Date();
		const timeZone = date.getTimezoneOffset() / 60;
		return this.checkTimeZone(timeZone);
	},
	getTimeBasedNumber(intervalSeconds = 5) {
		const now = Date.now(); // current timestamp in ms
		const interval = intervalSeconds * 1000; // interval in ms
		return Math.floor(now / interval);
	},

	/** Generates hash based on timestamp
	 * @param {Number} duration - Each new  generated hash will be valid during provided duration(in millisecond)
	 */
	generateCacheHash(duration) {
		if (duration > 0) {
			return Math.floor(Date.now() / duration);
		}

		return Date.now();
	},

	/** Move item in array
	 * @param {string} from - The start position moved
	 * @param {string} to - The array  where item will be moved
	 * @param {string} result - Array
	 */

	move: (from, to, ...a) => {
		a.splice(to, 0, ...a.splice(from, 1));
		return a;
	},

	insert: (arr, index, newItem) => [...arr.slice(0, index), newItem, ...arr.slice(index)],

	isAllLanguage(languageId) {
		return languageId === "*";
	},

	shouldControlBeVisible(languageId, property) {
		return Utils.isAllLanguage(languageId) && property && property.isTranslatable === true;
	},

	getTemplateAreasFromArray(gridItems, rowHeight, contentAutoHeightList, rowsCount) {
		const gridTemplateString = [];
		const templateRows = [];
		const rowsCountArray = [];
		for (let i = 0; i < rowsCount; i++) {
			rowsCountArray.push(".");
		}

		// let c = 0;
		gridItems.forEach((it, c) => {
			let tempx = it.x;
			let deltaI = 0;
			for (let i = it.y; i < it.h + it.y + deltaI; i++) {
				if (!gridTemplateString[i]) gridTemplateString[i] = [...rowsCountArray];
				// if element is out of range move to next available line
				if (it.x >= gridTemplateString[i].length) {
					if (!deltaI) {
						deltaI = gridTemplateString.length - i;
						tempx = 0;
						i = gridTemplateString.length;
					}
					if (!gridTemplateString[i]) gridTemplateString[i] = [...rowsCountArray];
				}

				let isPlaceBusy = false;
				for (let j = tempx; j < tempx + it.w; j++) {
					// if elements place already filled
					if (!deltaI && gridTemplateString[i][j] !== "." && !isPlaceBusy) {
						isPlaceBusy = true;
						deltaI = gridTemplateString.length - i;
						tempx = 0;
						i = gridTemplateString.length;
						if (!gridTemplateString[i]) gridTemplateString[i] = [...rowsCountArray];
					}

					// if elements is too long, cut at the end of line
					if (j >= gridTemplateString[i].length) break;
					gridTemplateString[i][j] = String.fromCharCode(97 + c);
					const isAutoHeight = contentAutoHeightList.some(item => item === it.content);
					if (!templateRows[i] || isAutoHeight) {
						isAutoHeight ? (templateRows[i] = "auto") : (templateRows[i] = `${rowHeight}px`);
					}
				}
			}
		});

		// fill all missing lines with dots
		for (let i = 0; i < gridTemplateString.length; i++) {
			if (!gridTemplateString[i]) gridTemplateString[i] = [...rowsCountArray];
		}
		return {
			gridTemplateRowsCount: gridTemplateString.length,
			gridTemplateString: gridTemplateString.reduce(
				(acc, curr) => `${acc + (acc ? "\n\r" : "")}"${curr.join(" ")}"`,
				"",
			),
			templateRows,
		};
	},

	objectRemoveEmpty(obj) {
		Object.keys(obj).forEach(key => {
			if (!obj[key] && typeof obj[key] !== "number" && typeof obj[key] !== "boolean") delete obj[key];
		});
		return obj;
	},
	capitalize(s) {
		if (typeof s !== "string") return "";
		return s.charAt(0).toUpperCase() + s.slice(1);
	},
	lowarcase(s) {
		if (typeof s !== "string") return "";
		return s.charAt(0).toLowerCase() + s.slice(1);
	},
	isFarsiLanguage(languageId) {
		const FarsiLanguages = ["fa", "ar", "ku", "he", "ur-PK", "aeb"];
		return FarsiLanguages.indexOf(languageId) !== -1;
	},
	getKeyByValue(object, value) {
		return Object.keys(object).find(key => object[key] === value);
	},
	tooltipWrapper(element, config = {}) {
		const { message = "", placement = "bottomRight", conditions = [] } = config;
		const isWrapping = Array.isArray(conditions) ? conditions.every(condition => condition) : conditions;
		return isWrapping ? (
			<Tooltip placement={placement} title={message}>
				{element}
			</Tooltip>
		) : (
			element
		);
	},
	isBase64(str) {
		if (typeof str !== "string" || str === "" || str.trim() === "") {
			return false;
		}
		try {
			return btoa(atob(str)) === str;
		} catch (err) {
			return false;
		}
	},
	async copyToClipboard(text) {
		try {
			if (navigator.clipboard && navigator.clipboard.writeText) {
				await navigator.clipboard.writeText(text);
			} else {
				const el = document.createElement("textarea");
				el.value = text;
				document.body.appendChild(el);
				el.focus();
				el.select();
				document.execCommand("copy");
				document.body.removeChild(el);
			}
		} catch (error) {
			console.error(error);
		}
	},
	async getBase64(file) {
		const promise = new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				resolve(reader.result);
			};
			reader.onerror = error => {
				reject(error);
			};
		});

		const base64 = await promise;
		return base64;
	},

	debounce(func, wait) {
		let timeout;
		return function debounced(...args) {
			const context = this;
			if (timeout) clearTimeout(timeout);
			timeout = setTimeout(() => {
				timeout = null;
				func.apply(context, args);
			}, wait);
		};
	},

	isBase64Image(url) {
		return /^data:image\/(png|jpeg|jpg|gif|bmp|webp);base64,/.test(url);
	},
};
